import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cells from '../accountsCells.module.css';
import {
    Button,
    ColumnResizeHandle,
    DataTable,
    DropdownMenu,
    useResizableColumns,
    type ColumnConfig,
    type DropdownMenuItem,
} from '@/components/ui';

export interface AccountsTableProps<Row> {
    rows: Row[];
    columns: ColumnConfig<Row>[];
    rowKey: keyof Row | ((row: Row) => string);
    selectedKeys: string[];
    onSelectedKeysChange: (keys: string[]) => void;
    sortedInfo?: { field: string; order: 'ascend' | 'descend' | null };
    onSortChange?: (ordering: string) => void;
    /** Absent ⇒ no "Xem" button AND no row click. Present ⇒ both, and — unless
     *  `onRowClick` below overrides the row half — they open the same thing; the
     *  button is the keyboard path, the row click is a mouse convenience.
     *
     *  EAA-1839: this used to add "(org screen: no `GET /accounts/{sub}`-style
     *  detail read exists on Assignment rows yet)". It does now —
     *  `ScopedAccountProfileModal` reads the profile for an Assignment row — so
     *  the org board passes this and gets both paths, both opening that modal. */
    onViewDetail?: (row: Row) => void;
    /**
     * EAA-1839: where a ROW click goes, when that must differ from what the "Xem"
     * button does. Omitted, a row click falls back to `onViewDetail`, so every
     * existing caller behaves exactly as before.
     *
     * The realm board needs the split: clicking a row opens a detail MODAL over the
     * list, while "Xem" navigates to `/console/accounts/:sub`. Both are deliberate
     * (see `AccountsPage`'s docstring), and with one callback they were
     * indistinguishable from here.
     */
    onRowClick?: (row: Row) => void;
    /** Row-level menu beyond "Xem" — empty array renders no kebab for that row. */
    rowActions: (row: Row) => DropdownMenuItem[];
    rowActionsAriaLabel: (row: Row) => string;
    emptyText: string;
    /**
     * EAA-1839 (frame 35): per-row tone. Frame 35's table paints two rows
     * differently — `#fffafa` for a grant whose Level is paused/withdrawn, and
     * `#fcfcfd` for one that is revoked — and those are STATES OF THE ROW, so they
     * belong on the row element rather than on a cell inside it. Plain pass-through
     * to `DataTable.rowClassName`, which this wrapper did not forward.
     */
    rowClassName?: (row: Row, index: number) => string;
    /** EAA-1839 (frame 35): the "Mật độ" switch — an extra class on the table card. */
    className?: string;
    /**
     * EAA-1839: opt into user-draggable column widths, remembered per browser.
     *
     * `storageKey` is namespaced by the kit under `ui:colwidths:` and must be
     * distinct PER TABLE, not per screen — the realm and org boards have different
     * columns, so sharing one key would let each overwrite the other's widths.
     * Bump `version` if the column set ever changes shape enough that stored widths
     * would land on the wrong columns; the kit discards other versions rather than
     * migrating them.
     *
     * Omitted ⇒ no grips, no storage read, and the table renders exactly as before.
     */
    resize?: { storageKey: string; version?: number };
}

/**
 * EAA-1839: what a row click must NOT hijack. A `<tr onClick>` fires for every
 * click anywhere inside the row, so each interactive descendant has to opt out
 * or it stops working. On this table that is, concretely:
 *
 *  - the selection checkbox (`.ant-table-selection-column`, `.ant-checkbox-wrapper`,
 *    the `input` inside it) — an unguarded row handler makes ticking a row
 *    impossible, since every tick would also open the profile modal;
 *  - the "Xem" row button and any other kit `Button` (`button`) — it already
 *    opens the modal itself, so an unguarded click would run the same open twice;
 *  - the row kebab (`DropdownMenu` → a `button.ant-dropdown-trigger`) — opening
 *    a menu is not opening the row;
 *  - the Level cell's popover trigger (`span.sa-level-cell`, focusable, matched
 *    generically by `[tabindex]:not([tabindex^="-"])` rather than by its app
 *    class, so this shared table keeps no per-screen knowledge);
 *  - the Level cell's amber "Chọn Level" chip, which opens the inline picker —
 *    the kit `Chip` renders `<span role="button">` when it has an `onClick`, so
 *    `[role="button"]` catches it (and any future interactive chip);
 *  - links, form controls and menu items in general (`a[href]`, `input`,
 *    `select`, `textarea`, `label`, `[role="menuitem"]`, `[role="checkbox"]`,
 *    `[role="switch"]`, `[aria-haspopup]`);
 *  - anything a caller explicitly marks with `[data-row-click-ignore]` — the
 *    escape hatch for a cell affordance none of the above describes.
 */
const ROW_CLICK_IGNORE_SELECTOR = [
    '[data-row-click-ignore]',
    'button',
    'a[href]',
    'input',
    'select',
    'textarea',
    'label',
    '[role="button"]',
    '[role="menuitem"]',
    '[role="checkbox"]',
    '[role="switch"]',
    '[aria-haspopup]',
    '[tabindex]:not([tabindex^="-"])',
    '.ant-table-selection-column',
    '.ant-checkbox-wrapper',
    '.ant-dropdown-trigger',
].join(',');

/**
 * EAA-1820: the ONE accounts table. Replaces `accounts/AccountsTable.tsx`
 * (realm, already on the kit's `DataTable` — since EAA-1804 renamed and moved
 * to `accounts/components/AccountsListTable.tsx`) and
 * `hierarchy/scopedAccounts/ScopedAccountsTable.tsx` (org, a hand-rolled
 * `<table>` with its own checkbox/sort-header/status-dot markup) — the org table
 * is migrated onto `DataTable` here rather than the other way around, so both
 * contexts get the same selection chrome, the same sort-caret affordance and the
 * same single scroll region (`shared/AccountsScreen`'s `.acc-table-scroll`
 * wrapper) instead of two hand-tuned equivalents that drift.
 *
 * Column CONTENT stays fully context-owned — each caller builds its own
 * `ColumnConfig<Row>[]` (see `accounts/components/AccountsListTable.tsx` and
 * `hierarchy/scopedAccounts/ScopedAccountsTable.tsx`, now thin column-builders
 * around this component) because the row shapes are genuinely different
 * entities: a Keycloak account vs. an `Assignment` grant. What is shared is the
 * chrome around whichever columns a context provides, plus the "Xem" button +
 * kebab pattern every row gets.
 *
 * EAA-1804: the "Xem" row action swapped to the kit `Button` instead of a
 * hand-written `<button className="portal-btn ...">`; `acc-row-view` is kept
 * on `className` since it carries the row-action layout CSS.
 *
 * EAA-1839: optional draggable column widths via the kit's headless
 * `useResizableColumns` + `ColumnResizeHandle` (see the `resize` prop). Built on
 * the kit rather than hand-rolled because `MatrixTable` already drives the same
 * pair, so the grip's hit area, its keyboard operation (arrows nudge, Shift ×10,
 * double-click resets) and its `localStorage` shape are shared instead of
 * reimplemented per table.
 */
export function AccountsTable<Row extends object>({
    rows,
    columns,
    rowKey,
    selectedKeys,
    onSelectedKeysChange,
    sortedInfo,
    onSortChange,
    onViewDetail,
    onRowClick,
    rowActions,
    rowActionsAriaLabel,
    emptyText,
    rowClassName,
    className,
    resize,
}: AccountsTableProps<Row>) {
    const { t } = useTranslation();

    /**
     * EAA-1839: clicking anywhere in the row opens the same detail the "Xem"
     * button opens — but only when the click did not land on something
     * interactive (see `ROW_CLICK_IGNORE_SELECTOR` for the exact list and why
     * each entry is there). `closest()` is used rather than comparing
     * `event.target` to the row, because the click target is normally a text
     * node's parent deep inside a cell.
     *
     * KEYBOARD: this is a mouse convenience only — a `<tr>` is not focusable
     * and is deliberately not turned into a fake button. The "Xem" button in
     * the actions cell stays, and remains the keyboard/screen-reader path.
     */
    const handleRowClick = useCallback(
        (row: Row, event: React.MouseEvent<HTMLElement>) => {
            // `onRowClick` first, falling back to `onViewDetail` — so a caller that
            // wants the row and the "Xem" button to do DIFFERENT things can say so,
            // and one that does not keeps the previous single-callback behaviour.
            const handler = onRowClick ?? onViewDetail;
            if (!handler) return;
            const target = event.target as HTMLElement | null;
            if (target?.closest(ROW_CLICK_IGNORE_SELECTOR)) return;
            handler(row);
        },
        [onRowClick, onViewDetail]
    );

    /**
     * EAA-1839: ONLY columns that declare a numeric `width` become resizable, and
     * that is the whole trick rather than an implementation shortcut. A column left
     * without a width stays auto-sized, so it absorbs whatever space the fixed ones
     * do not claim — which is what keeps the identity column readable. Making every
     * column a fixed number instead is what squeezed it to "g…" / "ab…": once all
     * widths are fixed, nothing flexes and the widest content simply gets clipped.
     */
    const resizableColumns = useMemo(
        () =>
            columns
                .filter((column) => typeof column.width === 'number')
                .map((column) => ({ key: column.key, width: column.width as number, min: 80 })),
        [columns]
    );

    // `storage: null` is the hook's own documented escape hatch, and it is the
    // right way to express "this table opted out": passing a placeholder
    // `storageKey` instead would leave the hook reading a junk `localStorage` entry
    // on every mount of every non-resizable accounts table.
    const { widths, startResize, nudge, reset, resizingKey } = useResizableColumns({
        storageKey: resize?.storageKey ?? 'unused',
        columns: resize ? resizableColumns : [],
        version: resize?.version,
        storage: resize ? undefined : null,
    });

    const fullColumns: ColumnConfig<Row>[] = columns.map((column) => {
        const resizable = Boolean(resize) && typeof column.width === 'number';
        if (!resizable) return column;
        // `ColumnResizeHandle` is positioned `absolute`, so it anchors to the
        // nearest positioned ancestor. The sticky `<th>` rule in
        // `15-accounts.css` supplies that (a sticky box is a positioned box) —
        // without it every grip would fly to the table's own corner.
        const label = typeof column.title === 'string' ? column.title : column.key;
        return {
            ...column,
            width: widths[column.key] ?? column.width,
            // A fragment, not a wrapper element: the grip is `position: absolute`
            // and must anchor to the `<th>` itself so it spans the full header
            // height. A wrapper span would either be `static` (harmless but
            // pointless) or become the anchor and shrink the grip to the text's
            // box. This also keeps the change CSS-free.
            title: (
                <>
                    {column.title}
                    <ColumnResizeHandle
                        columnKey={column.key}
                        startResize={startResize}
                        nudge={nudge}
                        active={resizingKey === column.key}
                        label={t('console.accounts.table.resizeColumnAriaLabel', { column: label })}
                        // `reset` clears EVERY stored width for this table, not just
                        // this column — the kit exposes no per-column reset. That is
                        // the behaviour double-click and Enter get here; a
                        // per-column undo would need a change in `packages/ui`.
                        onReset={reset}
                    />
                </>
            ),
        };
    });

    return (
        <div className="acc-table-scroll">
            <DataTable<Row>
                /**
                 * EAA-1839: THE SAFETY NET AGAINST CELLS PAINTING OVER THEIR NEIGHBOUR.
                 *
                 * `DataTable` gives antd a `scroll.x`, which puts the table on
                 * `table-layout: fixed`, so every `<td>` has a DEFINITE width. A `<td>`
                 * does not clip its content, so any cell whose content is wider than
                 * its column simply overflows and paints on top of the next column —
                 * which is exactly what "Chờ đăng nhập lần đầu" (Trạng thái) did over
                 * "Chưa ghi nhận" (Đăng nhập cuối) on every row of /console/accounts,
                 * and what was patched per-column TWICE on /console/hierarchy/accounts
                 * before this. Tuning a column's `width` only lowers the odds; it never
                 * removes the failure mode, because the content is translated and the
                 * columns are user-resizable.
                 *
                 * `overflow: hidden` ALONE is the mechanism. Deliberately NO
                 * `white-space: nowrap` and NO `text-overflow: ellipsis`: several cells
                 * here are intentionally multi-line or multi-element (the identity cell
                 * is name-over-username, the status cell is dot + label, the Level cell
                 * owns its own inner truncation), and `nowrap` would flatten all three.
                 * A too-narrow cell now clips instead of overlapping — wrong-looking,
                 * but never unreadable-on-top-of-something-else.
                 *
                 * `styles.body.cell` is antd's semantic-DOM seam and lands on the body
                 * `<td>` itself, so this needs no CSS and — the point — no descendant
                 * selector reaching into `DataTable`'s internals.
                 *
                 * WHY HERE AND NOT IN `packages/ui/DataTable`: the failure is inherent
                 * to `table-layout: fixed` and so arguably belongs in the kit as an
                 * overridable default. It is set here instead because `packages/ui` is
                 * also consumed by `apps/web-content`, whose tables cannot be verified
                 * from this branch; this keeps the blast radius to the two accounts
                 * boards plus the app-detail accounts tab. Promote it to the kit's
                 * default once someone can check the other consumer.
                 */
                styles={{ body: { cell: { overflow: 'hidden' } } }}
                className={'portal-accounts-table-card' + (className ? ' ' + className : '')}
                /* EAA-1839: the actions column held 160px for a "Xem" button and a
                   kebab, which fits in 120. Under `table-layout: fixed` those 40px
                   came straight out of whichever column had no declared width — and
                   that was the identity column, which collapsed to 0. Cap it. */
                actionsWidth={120}
                rowClassName={rowClassName}
                // Only when a detail view exists at all — the org screen's
                // Assignment rows have one, and a table without `onViewDetail`
                // must not gain a click target that does nothing.
                onRowClick={onRowClick || onViewDetail ? handleRowClick : undefined}
                data={rows}
                columns={fullColumns}
                rowKey={rowKey}
                pagination={false}
                emptyText={emptyText}
                sortedInfo={sortedInfo}
                onSortChange={onSortChange}
                rowSelection={{
                    selectedRowKeys: selectedKeys,
                    onChange: (keys) => onSelectedKeysChange(keys as string[]),
                }}
                /* EAA-1839: hug the checkbox instead of antd's default ~36px+
                   selection column (a 16px floating in a much wider cell). See
                   `DataTable.types.ts`'s `compactSelectionColumn` doc for why no
                   separate header-cell fix was needed. */
                compactSelectionColumn
                actions={(row) => {
                    const items = rowActions(row);
                    return (
                        <span className={cells.tdActions}>
                            {onViewDetail && (
                                <Button className={cells.rowView} onClick={() => onViewDetail(row)}>
                                    {t('console.accounts.table.viewAction')}
                                </Button>
                            )}
                            {items.length > 0 && (
                                <DropdownMenu
                                    triggerAriaLabel={rowActionsAriaLabel(row)}
                                    items={items}
                                />
                            )}
                        </span>
                    );
                }}
            />
        </div>
    );
}

export default AccountsTable;
